/*
  Pilgrim video standard — cosmetic layer over Horizon's NATIVE autoplay.
  Owner: PILGRIM. Loaded site-wide via snippets/pilgrim-video.liquid (type="module").

  Horizon renders an autoplaying <video> straight into the page when a block's
  "Auto Play" setting is on — and we flip that default to on in blocks/video.liquid,
  blocks/_media.liquid, blocks/_media-without-appearance.liquid, and force it for
  the product gallery in snippets/product-media.liquid. Parser-inserted muted
  autoplay is reliable, so this script does NOT clone or force-play anything (an
  earlier version cloned the deferred-media <template>, and script-inserted muted
  videos are denied autoplay until a user gesture — they sat on a frozen frame).

  All we do here is the cosmetic layer:
    - strip the native controls + poster/hover overlays (CSS does the hiding),
    - add a minimal play/pause + mute/unmute cluster on foreground videos
      (product-gallery videos read as animated images — no cluster),
    - respect prefers-reduced-motion (pause; the cluster's play button opts in),
    - re-start in-view videos after a dialog closes (native deferred-media pauses
      its video on dialog:close / tab hide).

  Videos whose "Auto Play" is off stay as Horizon's native click-to-play.
*/

const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
const CONFIG = window.PilgrimVideoConfig || {};
const LABELS = Object.assign(
  { play: 'Play video', pause: 'Pause video', mute: 'Mute', unmute: 'Unmute' },
  CONFIG.labels || {}
);

const ICONS = {
  play: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 5.14v13.72a1 1 0 0 0 1.54.84l10.79-6.86a1 1 0 0 0 0-1.68L9.54 4.3A1 1 0 0 0 8 5.14Z"/></svg>',
  pause:
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M7 4h3.2v16H7zM13.8 4H17v16h-3.2z"/></svg>',
  muted:
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M11 5 6.2 9H3v6h3.2L11 19z"/><path d="m15.5 9.5 5 5m0-5-5 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
  unmuted:
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M11 5 6.2 9H3v6h3.2L11 19z"/><path d="M15.4 8.6a4.5 4.5 0 0 1 0 6.8M18 6a8 8 0 0 1 0 12" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
};

const isReduce = () => REDUCE.matches;

/** Product gallery videos read as animated images — no control cluster. */
function isGallery(dm) {
  return Boolean(dm.querySelector('.video-interaction-hint')) || Boolean(dm.closest('.product-media, media-gallery'));
}

/**
 * Only Vimeo embeds get our cluster. Vimeo strips to a clean frame (no native
 * controls), so it needs ours. YouTube can't be fully stripped — its title /
 * settings persist — so our buttons would just overlap YouTube's own chrome.
 */
function isVimeoEmbed(iframe) {
  return (iframe.dataset.videoType || iframe.src || '').toLowerCase().includes('vimeo');
}

function playSafe(video) {
  const result = video.play && video.play();
  if (result && typeof result.catch === 'function') {
    result.catch((error) => {
      if (error && error.name === 'NotAllowedError') markWaiting(video);
    });
  }
}

/*
  Autoplay refused (2026-10-14). iPhone/Mac Low Power Mode makes WebKit (every
  iOS browser, Safari on the Mac) refuse play() even for muted inline video;
  Firefox and browsers set to block autoplay do the same. WebKit then draws its
  own play icon inside the <video>, under whatever sits on top — a video link,
  a linked group, a collection-spot or product-card link, the gallery zoom — so
  a tap followed the link and the video never played.

  One rule for every video on the site:
    - a refused video is "waiting": data-pilgrim-waiting on the <video>, a
      centred play icon (a sign, not a button — it can sit inside a link) and
      no corner cluster; WebKit's own icon is hidden (CSS);
    - the first pointer tap anywhere on a waiting video starts it (a tap is a
      user gesture, which the browser allows) and swallows the click, so the
      link / zoom does not fire; from then on taps behave as normal;
    - once it plays, the icon goes and the corner cluster takes over.
  Keyboard activation (click with detail 0) is never intercepted.

  Refusals are seen three ways: playSafe() (all of this file's play calls), a
  sweep that calls play() on every visible paused autoplay <video> (native
  autoplay fails silently — Horizon's background videos, gallery), and a
  `pilgrim:autoplay-refused` event other Pilgrim scripts dispatch (Reveal).
*/
const waitingVideos = new Set();

function markWaiting(video) {
  if (!(video instanceof HTMLVideoElement) || isReduce() || waitingVideos.has(video)) return;
  const host = video.parentElement;
  if (!host) return;
  waitingVideos.add(video);
  video.dataset.pilgrimWaiting = 'true';

  if (!host.querySelector(':scope > .pilgrim-video-start')) {
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    const icon = document.createElement('span');
    icon.className = 'pilgrim-video-start';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = ICONS.play;
    video.after(icon);
  }

  video.addEventListener('playing', () => clearWaiting(video), { once: true });
}

function clearWaiting(video) {
  waitingVideos.delete(video);
  delete video.dataset.pilgrimWaiting;
  video.parentElement?.querySelector(':scope > .pilgrim-video-start')?.remove();
}

/** The waiting video under a pointer tap, if the tap belongs to it. */
function waitingVideoAt(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return null;
  // Real controls over a video (quick-add, form fields) keep their job; one
  // that wraps the video (a "Shop the look" collection spot) is the surface.
  const control = target.closest('button, input, select, textarea, label, summary');
  for (const video of waitingVideos) {
    if (!video.isConnected) {
      waitingVideos.delete(video);
      continue;
    }
    if (video.getClientRects().length === 0) continue;
    if (control && !control.contains(video)) continue;
    const rect = video.getBoundingClientRect();
    if (
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom
    ) {
      continue;
    }
    // Same section and same dialog: a sticky header or an open drawer above
    // the video is not "on" it.
    if (target.closest('dialog') !== video.closest('dialog')) continue;
    if (target.closest('.shopify-section') !== video.closest('.shopify-section')) continue;
    return video;
  }
  return null;
}

function onWaitingTap(event) {
  if (event.detail === 0 || waitingVideos.size === 0) return;
  const video = waitingVideoAt(event);
  if (!video) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  delete video.dataset.pilgrimUserPaused;
  video.muted = true;
  playSafe(video);
}

/*
  Sweep: native autoplay never reports a refusal, so play() each autoplay video
  once it is half on screen (or fills half the viewport) and still paused.
  Already playing or about to = harmless; a user pause is respected.
*/
const autoplaySweep =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            const box = entry.boundingClientRect;
            const seen = entry.intersectionRect;
            const rootHeight = entry.rootBounds ? entry.rootBounds.height : window.innerHeight;
            const onScreen =
              entry.isIntersecting &&
              box.width > 0 &&
              seen.width >= box.width * 0.5 &&
              (seen.height >= box.height * 0.5 || seen.height >= rootHeight * 0.5);
            if (!onScreen || !video.paused || isReduce() || video.dataset.pilgrimUserPaused === 'true') return;
            if (video.closest('dialog:not([open])')) return;
            playSafe(video);
          });
        },
        { threshold: [0, 0.25, 0.5, 0.75, 1] }
      )
    : null;

function sweepAutoplay(root) {
  if (!autoplaySweep) return;
  const scope = root && root.querySelectorAll ? root : document;
  if (scope instanceof HTMLVideoElement && scope.autoplay) autoplaySweep.observe(scope);
  scope.querySelectorAll('video[autoplay]').forEach((video) => autoplaySweep.observe(video));
}

function normalizeVideo(video) {
  video.muted = true;
  video.defaultMuted = true;
  video.setAttribute('muted', '');
  video.loop = true;
  video.setAttribute('loop', '');
  video.playsInline = true;
  video.setAttribute('playsinline', '');
  video.removeAttribute('controls');
}

function createButton(label, html) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'pilgrim-video-controls__btn';
  btn.setAttribute('aria-label', label);
  btn.innerHTML = html;
  return btn;
}

/**
 * Merchant visibility flags for the cluster buttons — the Video block's
 * show_play_pause / show_mute settings, rendered as data attributes by
 * snippets/video.liquid. Absent attribute = shown, so blocks that don't pass
 * the settings (Media block, product gallery paths) keep both buttons.
 * Reduced motion overrides a hidden play button: it's the opt-in control for
 * users whose videos deliberately don't autostart.
 * (Replaced the old audio-track detection, which Chrome never supported.)
 */
function clusterFlags(dm) {
  return {
    hidePlay: dm.hasAttribute('data-pilgrim-hide-play') && !isReduce(),
    hideMute: dm.hasAttribute('data-pilgrim-hide-mute'),
  };
}

function addControls(dm, video) {
  if (dm.querySelector(':scope > .pilgrim-video-controls')) return;
  const { hidePlay, hideMute } = clusterFlags(dm);
  if (hidePlay && hideMute) return;

  const cluster = document.createElement('div');
  cluster.className = 'pilgrim-video-controls';

  const playButton = createButton(LABELS.pause, ICONS.pause);
  const muteButton = createButton(LABELS.unmute, ICONS.muted);

  const syncPlay = () => {
    const playing = !video.paused;
    playButton.innerHTML = playing ? ICONS.pause : ICONS.play;
    playButton.setAttribute('aria-label', playing ? LABELS.pause : LABELS.play);
  };
  const syncMute = () => {
    muteButton.innerHTML = video.muted ? ICONS.muted : ICONS.unmuted;
    muteButton.setAttribute('aria-label', video.muted ? LABELS.unmute : LABELS.mute);
  };

  playButton.addEventListener('click', () => {
    if (video.paused) {
      delete video.dataset.pilgrimUserPaused;
      playSafe(video);
    } else {
      // Remember a deliberate pause so the off-screen observer and resume helpers
      // don't override it when the video scrolls back into view.
      video.dataset.pilgrimUserPaused = 'true';
      video.pause();
    }
  });
  muteButton.addEventListener('click', () => {
    video.muted = !video.muted;
    if (!video.muted && video.volume === 0) video.volume = 1;
    syncMute();
  });

  video.addEventListener('play', syncPlay);
  video.addEventListener('pause', syncPlay);
  video.addEventListener('volumechange', syncMute);

  syncPlay();
  syncMute();

  if (!hidePlay) cluster.append(playButton);
  if (!hideMute) cluster.append(muteButton);
  dm.appendChild(cluster);
}

/**
 * Controls for an inline external embed (YouTube/Vimeo <iframe>) — same look as
 * the uploaded-video cluster, but drives the player via postMessage (the embed
 * carries enablejsapi/api from snippets/video.liquid). State is optimistic: the
 * embed autoplays muted + looping, so we just track our own toggles.
 */
function addIframeControls(dm, iframe) {
  if (dm.querySelector(':scope > .pilgrim-video-controls')) return;
  const { hidePlay, hideMute } = clusterFlags(dm);
  if (hidePlay && hideMute) return;

  const isVimeo = isVimeoEmbed(iframe);
  const post = (msg) => iframe.contentWindow && iframe.contentWindow.postMessage(JSON.stringify(msg), '*');
  const command = {
    play: () => (isVimeo ? post({ method: 'play' }) : post({ event: 'command', func: 'playVideo', args: '' })),
    pause: () => (isVimeo ? post({ method: 'pause' }) : post({ event: 'command', func: 'pauseVideo', args: '' })),
    mute: () => (isVimeo ? post({ method: 'setMuted', value: true }) : post({ event: 'command', func: 'mute', args: '' })),
    unmute: () => (isVimeo ? post({ method: 'setMuted', value: false }) : post({ event: 'command', func: 'unMute', args: '' })),
  };

  let playing = true;
  let muted = true;
  const cluster = document.createElement('div');
  cluster.className = 'pilgrim-video-controls';
  const playButton = createButton(LABELS.pause, ICONS.pause);
  const muteButton = createButton(LABELS.unmute, ICONS.muted);

  const syncPlay = () => {
    playButton.innerHTML = playing ? ICONS.pause : ICONS.play;
    playButton.setAttribute('aria-label', playing ? LABELS.pause : LABELS.play);
  };
  const syncMute = () => {
    muteButton.innerHTML = muted ? ICONS.muted : ICONS.unmuted;
    muteButton.setAttribute('aria-label', muted ? LABELS.unmute : LABELS.mute);
  };

  playButton.addEventListener('click', () => {
    playing = !playing;
    (playing ? command.play : command.pause)();
    syncPlay();
  });
  muteButton.addEventListener('click', () => {
    muted = !muted;
    (muted ? command.mute : command.unmute)();
    syncMute();
  });

  if (!hidePlay) cluster.append(playButton);
  if (!hideMute) cluster.append(muteButton);
  dm.appendChild(cluster);

  if (isReduce()) {
    playing = false;
    syncPlay();
    const stop = () => command.pause();
    iframe.addEventListener('load', () => window.setTimeout(stop, 300), { once: true });
    window.setTimeout(stop, 1000);
  }
}

/**
 * Clean up one <deferred-media> that Horizon rendered with an inline autoplaying
 * <video> or external <iframe>. Non-autoplay videos keep the media inside a
 * <template> (no inline child) and are left as Horizon's native click-to-play.
 */
function process(dm) {
  if (!(dm instanceof HTMLElement) || dm.tagName !== 'DEFERRED-MEDIA') return;
  if (dm.dataset.pilgrimVideo === 'managed') return;

  const video = dm.querySelector(':scope > video');
  const iframe = video ? null : dm.querySelector(':scope > iframe');
  if (!video && !iframe) return;

  // Disable the native click-loader so a stray click can't clone a duplicate,
  // and let Horizon's own CSS fade the poster image (deferred-media[data-media-loaded]).
  dm.setAttribute('data-media-loaded', 'true');
  dm.dataset.pilgrimVideo = 'managed';

  if (video) {
    normalizeVideo(video);
    if (isReduce()) {
      // Honour reduced motion: no auto-motion. The cluster's play button opts in.
      video.removeAttribute('autoplay');
      try {
        video.pause();
      } catch {
        /* no-op */
      }
    }
    if (!isGallery(dm)) {
      addControls(dm, video);
      observeOffscreen(video);
    }
  } else if (!isGallery(dm) && isVimeoEmbed(iframe)) {
    // Vimeo only — see isVimeoEmbed. YouTube is left with its own (un-strippable)
    // chrome; the iframe stays managed (poster faded) but gets no Pilgrim cluster.
    addIframeControls(dm, iframe);
  }
}

function scan(root) {
  const scope = root && root.querySelectorAll ? root : document;
  scope.querySelectorAll('deferred-media').forEach(process);
  scope.querySelectorAll('.pilgrim-spot-video, .pilgrim-lazy-video').forEach(observeSpotVideo);
  scope.querySelectorAll('deferred-media[data-pilgrim-lazy]').forEach(observeLazyMedia);
  sweepAutoplay(scope);
}

/*
  Lazy videos (2026-09-17). snippets/pilgrim-collection-spot.liquid (`.pilgrim-spot-video`)
  and the Video (PILGRIM) block's mobile variant (`.pilgrim-lazy-video`) render
  a video spot as its poster plus an inert <template> holding the native
  <video-background-component> (whose connectedCallback attaches the sources and
  calls load()). Before, that component sat in the markup from the start, so
  every spot video buffered ~10 s of HLS at page load wherever it was — 2.4 MB
  on the production earrings page, 13 MB on staging — and a spot's hidden
  breakpoint variant loaded too. Now the component is instantiated when the
  spot comes within half a viewport height, paused again when it is further than that
  away, and a display:none variant never gets a box, so it never
  intersects. Reduced motion keeps the poster. Theme editor: instantiate at
  once so merchants see what they placed.
*/
const spotObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const wrap = entry.target;
            if (entry.isIntersecting) {
              if (isReduce()) return;
              instantiateSpotVideo(wrap);
              const video = wrap.querySelector('video');
              if (video && video.paused && video.dataset.pilgrimUserPaused !== 'true') {
                video.muted = true;
                playSafe(video);
              }
            } else {
              const video = wrap.querySelector('video');
              if (video && !video.paused) {
                try {
                  video.pause();
                } catch {
                  /* no-op */
                }
              }
            }
          });
        },
        { rootMargin: '50% 0px' }
      )
    : null;

function instantiateSpotVideo(wrap) {
  const template = wrap.querySelector('template.pilgrim-spot-video__template, template.pilgrim-lazy-video__template');
  if (!template) return;
  wrap.appendChild(template.content.cloneNode(true));
  template.remove();
}

function observeSpotVideo(wrap) {
  if (wrap.dataset.pilgrimSpotVideo) return;
  wrap.dataset.pilgrimSpotVideo = 'observed';
  if (window.Shopify && window.Shopify.designMode) {
    instantiateSpotVideo(wrap);
    return;
  }
  if (spotObserver) {
    spotObserver.observe(wrap);
  } else {
    instantiateSpotVideo(wrap);
  }
}

/*
  Lazy gallery videos (2026-09-17). The product gallery renders every video
  that is not the first carousel slide with `data-pilgrim-lazy`
  (snippets/video.liquid, video_lazy): poster + the native inert <template>,
  no inline <video>. Before, each one was an inline autoplaying <video>, and
  Chrome buffers ~10 s of those even in an unselected slide or a closed
  dialog — 4 slides + 4 zoom-dialog copies = 11.5 MB at page load on a
  production product page.

  We watch the media's container (the slide / grid item / zoom-dialog item —
  the slide's own content is content-visibility:hidden while unselected, the
  slide itself always has a box): when at least half of it is on screen the
  template is cloned in, handed to process() and played muted; when it leaves,
  the video pauses. A closed dialog has no box, so its copies never load.
  `slideshow:select` is a second trigger for the same activation. Reduced
  motion: never activated — the native poster button stays as click-to-play.
  Theme editor: same behaviour as the storefront.
*/
function lazyContainer(dm) {
  return dm.closest('slideshow-slide, .product-media-container') || dm;
}

function activateLazyMedia(dm) {
  if (isReduce() || dm.getAttribute('data-media-loaded')) return;
  const content = dm.querySelector(':scope > template')?.content.firstElementChild?.cloneNode(true);
  if (!content) return;
  // A cloned <video muted> does not reliably come out muted, and an unmuted
  // play() is refused — set the property before it connects.
  if (content instanceof HTMLVideoElement) content.muted = true;
  dm.appendChild(content);
  process(dm);
}

function setLazyMediaActive(dm, active) {
  if (active) activateLazyMedia(dm);
  const video = dm.querySelector(':scope > video');
  if (!video) return;
  if (!active) {
    if (!video.paused) video.pause();
  } else if (video.paused && !isReduce() && video.dataset.pilgrimUserPaused !== 'true') {
    video.muted = true;
    playSafe(video);
  }
}

const lazyMediaObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const box = entry.boundingClientRect;
            const seen = entry.intersectionRect;
            const rootHeight = entry.rootBounds ? entry.rootBounds.height : window.innerHeight;
            // Half its width (a peeking next slide is not "on screen") and half
            // its height — or half the viewport, for media taller than the screen.
            const active =
              entry.isIntersecting &&
              box.width > 0 &&
              seen.width >= box.width * 0.5 &&
              (seen.height >= box.height * 0.5 || seen.height >= rootHeight * 0.5);
            entry.target.querySelectorAll('deferred-media[data-pilgrim-lazy]').forEach((dm) => {
              if (lazyContainer(dm) === entry.target) setLazyMediaActive(dm, active);
            });
          });
        },
        { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] }
      )
    : null;

function observeLazyMedia(dm) {
  if (dm.dataset.pilgrimLazy) return;
  dm.dataset.pilgrimLazy = 'observed';
  if (lazyMediaObserver) {
    lazyMediaObserver.observe(lazyContainer(dm));
  } else {
    activateLazyMedia(dm);
  }
}

function inViewport(el) {
  const rect = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const vw = window.innerWidth || document.documentElement.clientWidth;
  return rect.bottom > 0 && rect.right > 0 && rect.top < vh && rect.left < vw;
}

/**
 * Native <deferred-media> pauses its video on dialog:close and tab hide. Our
 * videos are meant to stay on, so re-start the in-view managed ones (deferred a
 * frame, to land after the native pause).
 */
function resumeInView() {
  if (isReduce()) return;
  document.querySelectorAll('deferred-media[data-pilgrim-video="managed"] > video').forEach((video) => {
    if (video.paused && video.dataset.pilgrimUserPaused !== 'true' && inViewport(video)) playSafe(video);
  });
}

/**
 * Pause foreground autoplay videos while they're scrolled out of view, and
 * resume the ones WE paused when they return. Concurrent off-screen <video>s
 * otherwise keep decoding/looping — needless main-thread, battery and bandwidth
 * cost on long pages. A user pause (cluster) and reduced motion are left alone;
 * product-gallery videos aren't observed (the native gallery owns their playback).
 */
const offscreenObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting) {
              const wasOffscreenPaused = video.dataset.pilgrimOffscreenPaused === 'true';
              delete video.dataset.pilgrimOffscreenPaused;
              if (wasOffscreenPaused && video.paused && !isReduce() && video.dataset.pilgrimUserPaused !== 'true') {
                playSafe(video);
              }
            } else if (!video.paused) {
              video.dataset.pilgrimOffscreenPaused = 'true';
              try {
                video.pause();
              } catch {
                /* no-op */
              }
            }
          });
        },
        { rootMargin: '200px' }
      )
    : null;

function observeOffscreen(video) {
  if (offscreenObserver) offscreenObserver.observe(video);
}

const mutationObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== 1) return;
      if (node.matches && node.matches('deferred-media')) process(node);
      if (node.querySelectorAll) node.querySelectorAll('deferred-media').forEach(process);
      if (node.matches && node.matches('.pilgrim-spot-video, .pilgrim-lazy-video')) observeSpotVideo(node);
      if (node.querySelectorAll) node.querySelectorAll('.pilgrim-spot-video, .pilgrim-lazy-video').forEach(observeSpotVideo);
      if (node.matches && node.matches('deferred-media[data-pilgrim-lazy]')) observeLazyMedia(node);
      if (node.querySelectorAll) node.querySelectorAll('deferred-media[data-pilgrim-lazy]').forEach(observeLazyMedia);
      sweepAutoplay(node);
    });
  }
});

function boot() {
  scan(document);
  mutationObserver.observe(document.body, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  window.addEventListener('dialog:close', () => requestAnimationFrame(resumeInView));
  // Capture on window: runs before any link, gallery zoom or card handler.
  window.addEventListener('click', onWaitingTap, true);
  document.addEventListener('pilgrim:autoplay-refused', (event) => markWaiting(event.target));
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) requestAnimationFrame(resumeInView);
  });

  if (REDUCE.addEventListener) {
    REDUCE.addEventListener('change', (event) => {
      const videos = document.querySelectorAll('deferred-media[data-pilgrim-video="managed"] > video');
      if (event.matches) {
        videos.forEach((video) => {
          try {
            video.pause();
          } catch {
            /* no-op */
          }
        });
      } else {
        videos.forEach((video) => video.setAttribute('autoplay', ''));
        resumeInView();
      }
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

// Product-card carousels — and the quick-add drawer's gallery, which uses the
// same preview_image_only recipe — render videos as inert <template>s beside
// the poster (see snippets/product-media.liquid). When the carousel lands on
// such a slide, instantiate the video and play it muted in a loop; sliding
// away pauses it. The template only becomes a network request on first visit.
document.addEventListener('slideshow:select', (event) => {
  const slideshow = event.target;
  if (!(slideshow instanceof Element) || !slideshow.closest('.card-gallery, .pilgrim-quick-add-gallery')) return;

  for (const video of slideshow.querySelectorAll('video')) {
    try {
      video.pause();
    } catch {
      /* no-op */
    }
  }

  const slide = event.detail?.slide;
  if (!slide) return;

  const template = slide.querySelector('template.pilgrim-card-video');
  if (template) {
    template.parentElement.appendChild(template.content.cloneNode(true));
    template.remove();
  }

  const video = slide.querySelector('video');
  if (video) {
    video.muted = true;
    playSafe(video);
  }
});

// Product gallery: landing on a slide activates its lazy video (the observer
// above does the same once the slide is on screen) and pauses the others.
document.addEventListener('slideshow:select', (event) => {
  const slideshow = event.target;
  if (!(slideshow instanceof Element) || !slideshow.closest('media-gallery')) return;
  const slide = event.detail?.slide;
  if (!slide) return;
  slideshow.querySelectorAll('deferred-media[data-pilgrim-lazy]').forEach((dm) => {
    setLazyMediaActive(dm, slide.contains(dm));
  });
});
